import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TokenService } from '@core/auth/services/token.service';
import { ContextService } from '@core/context/services/context.service';
import { SKIP_ERROR_NOTIFICATION } from '@core/error-handling/services/http-error-notification.interceptor';
import { environment } from '@environments/environment';

export interface InstrumentoConfiguracaoEstabelecimento {
  permiteUtilizarParecer?: boolean;
  permiteUtilizarCompetencias?: boolean;
  permiteUtilizarConhecimentosConteudos?: boolean;
  permiteUtilizarHabilidadesCapacidades?: boolean;
  permiteUtilizarAtitudes?: boolean;
  permiteRecuperacaoParalela?: boolean;
  permiteRecuperacaoPeriodoAvaliativo?: boolean;
  permiteNotaComplementar?: boolean;
}

export interface InstrumentoPeriodoOption {
  id?: number;
  descricao?: string;
  dataInicial?: string;
  dataFinal?: string;
  modoAvaliacao?: string;
  tipoPeriodo?: string;
  periodoAvaliativo?: {
    id: number;
    descricao?: string;
    dataInicial?: string;
    dataFinal?: string;
  };
  [key: string]: unknown;
}

export interface InstrumentoPublicavel {
  publicado?: boolean;
  dataPublicacao?: string | null;
}

export interface InstrumentoAvaliacaoRegistro {
  id?: number;
  version?: number | null;
  versao?: number | null;
  descricao?: string;
  sigla?: string;
  data?: string;
  tipoInstrumento?: string;
  tipoPeriodo?: string;
  modoAvaliacao?: string;
  conteudo?: string | null;
  criteriosAvaliativos?: string | null;
  notaMinima?: number | null;
  notaMaxima?: number | null;
  peso?: number | null;
  turma?: { id: number; descricao?: string; tipoAtendimento?: string; etapaMatriz?: { modular?: boolean }; estabelecimento?: { id: number; nome?: string } };
  itemEducacional?: { id: number; descricao?: string; isCampoExperiencia?: boolean };
  periodoAvaliativo?: { id: number; descricao?: string; dataInicial?: string; dataFinal?: string } | null;
  instrumentoRecuperacao?: InstrumentoAvaliacaoRegistro | null;
  publicavel?: InstrumentoPublicavel;
  publicado?: boolean;
  publicar?: boolean;
  dataPublicacao?: string | null;
  notaMinimaConfiguracao?: number;
  notaMaximaConfiguracao?: number;
  quantidadeDecimais?: string;
  decimaisAvaliacao?: string;
  [key: string]: unknown;
}

export interface InstrumentoArquivoAnexo {
  id?: number | string;
  nome?: string;
  tipo?: string;
  tamanho?: number;
  arquivo?: string;
  origem?: string;
  pasta?: string;
  chave?: string;
  publicado?: boolean;
  [key: string]: unknown;
}

export interface InstrumentoTurmaContexto {
  id: number;
  descricao?: string;
  nome?: string;
  estabelecimento?: {
    id: number;
    nome?: string;
  };
  tipoAtendimento?: string;
  etapaMatriz?: {
    modular?: boolean;
  };
  [key: string]: unknown;
}

export interface InstrumentoItemEducacionalOption {
  id: number;
  descricao?: string;
  nome?: string;
  isCampoExperiencia?: boolean;
  etapaMatrizDisciplina?: {
    id: number;
  };
  [key: string]: unknown;
}

export interface InstrumentoAvaliacaoCommonsResponse {
  itensAvaliaveis?: InstrumentoAvaliacaoRegistro[];
  registrosAvaliacao?: Array<Record<string, unknown>>;
}

@Injectable({
  providedIn: 'root'
})
export class InstrumentoAvaliacaoService {
  private readonly apiUrl = environment.apiUrl;
  private readonly arquivosUrl = this.buildArquivosUrl();
  private readonly quietContext = new HttpContext().set(SKIP_ERROR_NOTIFICATION, true);

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private contextService: ContextService
  ) {}

  private buildArquivosUrl(): string {
    if (this.apiUrl.endsWith('/api')) {
      return `${this.apiUrl.slice(0, -4)}/zuul/api/commons/arquivos`;
    }

    return `${this.apiUrl}/commons/arquivos`;
  }

  private getContextTurmaId(): number | null {
    const turmaId = Number(this.contextService.getTurma()?.id || 0);
    return turmaId > 0 ? turmaId : null;
  }

  get(id: number): Observable<InstrumentoAvaliacaoRegistro> {
    return this.http.get<InstrumentoAvaliacaoRegistro>(
      `${this.apiUrl}/professores/instrumento-avaliacao/${id}`,
      { context: this.quietContext }
    );
  }

  list(params: { filter?: string; offset?: number; limit?: number; sort?: string; tipo?: string }): Observable<{ content: InstrumentoAvaliacaoRegistro[]; totalElements?: number }> {
    let httpParams = new HttpParams()
      .set('offset', String(params.offset ?? 0))
      .set('limit', String(params.limit ?? 999));

    if (params.filter?.trim()) {
      httpParams = httpParams.set('filter', params.filter.trim());
    }

    if (params.sort) {
      httpParams = httpParams.set('sort', params.sort);
    }

    return this.http.get<{ content: InstrumentoAvaliacaoRegistro[]; totalElements?: number }>(
      `${this.apiUrl}/professores/instrumento-avaliacao`,
      {
        params: httpParams,
        context: this.quietContext
      }
    );
  }

  getListContextualizada(params: {
    turmaId?: number | null;
    itemEducacionalId?: number | null;
    periodoAvaliativoId?: number | null;
    filter?: string;
    offset?: number;
    limit?: number;
    sort?: string;
    tipo?: string;
  }): Observable<{ content: InstrumentoAvaliacaoRegistro[]; totalElements?: number }> {
    let httpParams = new HttpParams();
    const turmaId = params.turmaId ?? this.getContextTurmaId();

    if (turmaId) {
      httpParams = httpParams.set('turma', String(turmaId));
    }

    if (params.itemEducacionalId) {
      httpParams = httpParams.set('itemEducacional', String(params.itemEducacionalId));
    }

    if (params.periodoAvaliativoId) {
      httpParams = httpParams.set('periodoAvaliativo', String(params.periodoAvaliativoId));
    }

    if (params.filter?.trim()) {
      httpParams = httpParams.set('filter', params.filter.trim().substring(0, 500));
    }

    return this.http.get<InstrumentoAvaliacaoCommonsResponse>(
      `${this.apiUrl}/commons/instrumento-avaliacao`,
      {
        params: httpParams,
        context: this.quietContext
      }
    ).pipe(
      map((response) => {
        const itens = (response?.itensAvaliaveis || []).filter((item) => this.isInstrumentoFromCommons(item));
        return {
          content: itens,
          totalElements: itens.length
        };
      })
    );
  }

  save(payload: InstrumentoAvaliacaoRegistro): Observable<InstrumentoAvaliacaoRegistro> {
    if (payload.id) {
      return this.http.put<InstrumentoAvaliacaoRegistro>(
        `${this.apiUrl}/professores/instrumento-avaliacao/${payload.id}`,
        payload
      );
    }

    return this.http.post<InstrumentoAvaliacaoRegistro>(
      `${this.apiUrl}/professores/instrumento-avaliacao`,
      payload
    );
  }

  updatePeriodoOrDate(payload: InstrumentoAvaliacaoRegistro): Observable<InstrumentoAvaliacaoRegistro> {
    return this.http.put<InstrumentoAvaliacaoRegistro>(
      `${this.apiUrl}/professores/instrumento-avaliacao/alterar-periodo-instrumento/${payload.id}`,
      payload
    );
  }

  remove(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/professores/instrumento-avaliacao/${id}`);
  }

  publicar(turmaId: number, id: number): Observable<InstrumentoAvaliacaoRegistro> {
    return this.http.put<InstrumentoAvaliacaoRegistro>(
      `${this.apiUrl}/commons/turma/${turmaId}/avaliacoes/publicar/${id}`,
      {}
    );
  }

  getConfiguracaoEstabelecimento(turmaId: number): Observable<InstrumentoConfiguracaoEstabelecimento> {
    return this.http.get<InstrumentoConfiguracaoEstabelecimento>(
      `${this.apiUrl}/commons/configuracoes-estabelecimentos/${turmaId}`,
      { context: this.quietContext }
    );
  }

  getDescricoesRecentes(limit = 5): Observable<Array<{ descricao: string }>> {
    return this.http.get<Array<{ descricao: string }>>(
      `${this.apiUrl}/professores/instrumento-avaliacao/descricoes-recentes`,
      {
        params: new HttpParams().set('limit', String(limit)),
        context: this.quietContext
      }
    );
  }

  getPeriodosItemAvaliacao(itemEducacionalId: number, existsModoAvaliacao = true): Observable<InstrumentoPeriodoOption[]> {
    let params = new HttpParams();

    if (existsModoAvaliacao) {
      params = params.set('filter', 'modoAvaliacao is not null');
    }

    return this.http.get<InstrumentoPeriodoOption[]>(
      `${this.apiUrl}/professores/item-educacional/${itemEducacionalId}/item-avaliacao-turma`,
      {
        params,
        context: this.quietContext
      }
    );
  }

  getTurmasContexto(
    sort = 'descricao',
    limit = 999
  ): Observable<{ content: InstrumentoTurmaContexto[]; totalElements?: number }> {
    const filter = '(modular != true or modular is null)';
    return this.http.get<{ content: InstrumentoTurmaContexto[]; totalElements?: number }>(
      `${this.apiUrl}/commons/turma`,
      {
        params: new HttpParams()
          .set('filter', filter)
          .set('sort', sort)
          .set('offset', '0')
          .set('limit', String(limit)),
        context: this.quietContext
      }
    );
  }

  getItensTurma(turmaId: number): Observable<InstrumentoItemEducacionalOption[]> {
    return this.http.get<InstrumentoItemEducacionalOption[]>(
      `${this.apiUrl}/commons/turma/${turmaId}/itens-educacionais`,
      { context: this.quietContext }
    );
  }

  getPeriodosAvaliativosTurma(turmaId: number): Observable<unknown[]> {
    return this.http.get<unknown[]>(
      `${this.apiUrl}/commons/periodos-avaliativos`,
      {
        params: new HttpParams().set('turma', String(turmaId)),
        context: this.quietContext
      }
    );
  }

  getCampoAdicional(id: number): Observable<Record<string, unknown> | null> {
    return this.http.get<Record<string, unknown> | null>(
      `${this.apiUrl}/professores/instrumento-avaliacao/campos-adicionais/${id}`,
      { context: this.quietContext }
    );
  }

  getCamposAdicionaisTemplate(): Observable<Record<string, unknown> | null> {
    return this.http.get<Record<string, unknown> | null>(
      `${this.apiUrl}/professores/instrumento-avaliacao/campos-adicionais`,
      { context: this.quietContext }
    );
  }

  private isInstrumentoFromCommons(item: InstrumentoAvaliacaoRegistro | null | undefined): boolean {
    if (!item) {
      return false;
    }

    const raw = item as Record<string, unknown>;
    const tipoPeriodo = String(item.tipoPeriodo || raw['tipoPeriodo'] || '').toUpperCase();
    const tipo = String(raw['tipo'] || '').toUpperCase();
    const tipoInstrumento = String(item.tipoInstrumento || raw['tipoInstrumento'] || '').toUpperCase();

    if (tipoPeriodo === 'INSTRUMENTO') {
      return true;
    }

    if (tipo === 'INSTRUMENTO') {
      return true;
    }

    return tipoInstrumento === 'INSTRUMENTO_AVALIACAO'
      || tipoInstrumento === 'RECUPERACAO_PARALELA'
      || tipoInstrumento === 'RECUPERACAO_PERIODO_AVALIATIVO'
      || tipoInstrumento === 'PONTO_EXTRA';
  }

  uploadAnexo(file: File): Observable<InstrumentoArquivoAnexo> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<InstrumentoArquivoAnexo>(this.arquivosUrl, formData);
  }

  getDownloadAnexoUrl(id: number | string): string | null {
    const token = this.tokenService.getAccessToken();
    const userAccess = this.contextService.getUserAccess();

    if (!token || !userAccess) {
      return null;
    }

    const params = new URLSearchParams();
    params.set('access_token', token);
    params.set('user_access', userAccess);

    return `${this.arquivosUrl}/${id}/download?${params.toString()}`;
  }
}
